import { Component, ElementRef, EventEmitter, forwardRef, Input, OnChanges, OnInit, Output, Renderer2, ViewChild } from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';

export const INPUT_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => InputComponent),
  multi: true
};

export const INPUT_VALIDATOR: any = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => InputComponent),
  multi: true
};

@Component({
  selector: 'sp-input',
  standalone: false,
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  providers: [INPUT_VALUE_ACCESSOR, INPUT_VALIDATOR]

})
export class InputComponent implements ControlValueAccessor, OnInit, Validator, OnChanges {
  @ViewChild('textInput', { static: true }) textInput: ElementRef;
  @Input() label: string = 'Input';
  @Input() name: string = 'Input';
  @Input() type: string = 'text';
  @Input() size: string = 'default';
  @Input() style: string = 'button-default';
  @Input() state: string = 'default';
  @Input() placeholder: string = '';
  @Input() specialPlaceholder: string = '+966';
  @Input() icon: string = null;
  @Input() value: string = null;
  @Input() required: boolean = false;
  @Input() multiple: string;
  @Input() disabled: boolean = false;
  @Input() valid: boolean = false;
  @Input() autofocus: boolean = false;
  @Input() warning: string = null;
  @Input() formMember: boolean = false; // to differenciate if an input has control or not and apply custom validation
  @Input() min = null; // min and max are for values, not characters
  @Input() max = null;
  @Input() minlength: string = null; // minlength and maxlength are for number of charactets
  @Input() maxlength: string = null;
  @Input() step: number = 1;
  @Input() photoUrl = null;
  loading: boolean = true;
  @Input() spinnerLoading: boolean = false;
  @Output() changeAction = new EventEmitter<any>();
  @Output() focusIn = new EventEmitter<any>();
  @Input() touched = false;
  errors: Array<string>;
  parseError: boolean;
  showPass: boolean;
  propagateChange = (_: any) => { };
  _onTouched = () => { };

  onChangeEvent(input) {
    let inputVal: any = '';
    let isValid = null;

    switch (this.type) {
      case 'number':
        isValid = parseFloat(this.value) > 0;
        inputVal = Number(input.target.value) ? Number(input.target.value) : input.target.value;
        break;
      case 'file':
        isValid = input.target.files.length > 0;
        if (isValid) {
          inputVal = Date.now().toString();
          if (input.target.files?.[0]) {
            inputVal = input.target.files[0].name;
          }
        }
        break;
      default:
        inputVal = input.target.value;
        isValid = inputVal;
        break;
    }
    if (this.value && isValid) {
      this.touched = true;
    } else {
      this.touched = false;
    }
    if (this.type === 'number' && input.target.value) {
      this.value = inputVal;
      this.propagateChange(this.value);
      this.changeAction.emit(input);
    } else if (this.type !== 'number') {
      this.value = inputVal;
      this.propagateChange(this.value);
      this.changeAction.emit(input);
    }
  }
  onBlur() {
    this._onTouched();
  }

  writeValue(v: any): void {
    if (v === undefined) {
      return;
    }
    this.value = v;
    if (v && this.type !== 'file') {
      this.value = v;
      this.touched = true;
    }
    if (v === '') {
      this.touched = false;
    }
  }

  registerOnChange(fn) {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any) {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.renderer.setProperty(this.textInput.nativeElement, 'disabled', isDisabled);
  }

  validate(control: UntypedFormControl) {
    if (this.formMember) {
      this.getFormValidationErrors(control);
      return !this.parseError
        ? null
        : {
          jsonParseError: {
            valid: false
          }
        };
    }
  }

  getFormValidationErrors(control) {
    this.errors = [];
    const controlErrors: ValidationErrors = control.errors;
    if (controlErrors != null) {
      this.state = 'error contain-icon';
      this.icon = 'interface-alert-circle';
      Object.keys(controlErrors).forEach(keyError => {
        this.errors.push(keyError);
      });
    } else {
      this.state = 'success contain-icon';
      this.icon = 'check-circle-2';
    }
  }

  numberAction(operation, step = 1) {
    if (this.disabled) {
      return;
    }
    let value = parseFloat(this.value || '0');

    switch (operation) {
      case 'add':
        if (this.max === undefined || this.max === null) {
          value = value + step;
        } else if (value < this.max) {
          value = value + step;
        }
        break;
      case 'subtract':
        if (value > this.min) {
          value = value - step;
        }
        break;
    }
    value = Number(value.toFixed(2));
    this.value = String(value);
    this.propagateChange(value);
    this.changeAction.emit(value);
    if (this.value || '0') {
      this.touched = true;
    }
    return value;
  }

  constructor(private renderer: Renderer2) { }

  ngOnInit() {
    if (this.type === 'number' && !this.value) {
      this.value = String(this.min || 0);
      this.propagateChange(this.value);
    }
    if (this.value) {
      this.touched = true;
    }
  }

  ngOnChanges() {
    this.renderer.setAttribute(this.textInput.nativeElement, 'maxlength', this.maxlength);
    this.renderer.setAttribute(this.textInput.nativeElement, 'minlength', this.minlength);
  }

  onFocus(input) {
    this.focusIn.emit(input);
  }
}

// implement the Input interface